// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { billingFrequencySchema } from '@/constants/billing-frequency';
import { chargeScheduleSchema } from '@/constants/charge-schedule';
import { invoiceModeSchema } from '@/constants/invoice-mode';
import { pricingModeSchema } from '@/constants/pricing-mode';
import { productFieldTypeSchema } from '@/constants/product-field-type';
import { SortDirection, sortDirectionSchema } from '@/constants/sort';
import { currencySchema, normalizedIso } from '@/currency/api/types';
import { isoCurrencyCodeSchema } from '@/currency/constants/iso';
import { ItemTypeFilter, itemTypeSchema } from '@/items/constants/item-types';
import { sparseItemSchema, sparsePersonSchema } from '@/sparse/api/types';
import { dateTimeSchema } from '@/utilities/date-time-schema';
import { integrationListSchema } from '@/utilities/integration';
import { searchTermSchema } from '@/utilities/search-term';
import { z } from 'zod';

/**
 * Schema for a unified item in the Item Catalog.
 * Combines products (SERVICE, DEVICE) and parts (BULK) into a single shape.
 */
export const itemSchema = z.object({
  id: z.string(),
  key: z.string(),
  type: itemTypeSchema,
  sku: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  assetCount: z.number().nullable(),
  lastUpdatedAt: dateTimeSchema.nullable(),
  lastUpdatedBy: sparsePersonSchema,
  isArchived: z.boolean(),
  shouldRenderSerialFields: z.boolean(),
  integrations: integrationListSchema,
});

export type Item = z.infer<typeof itemSchema>;

/**
 * Sort options for the items list.
 */
export type ItemSortField = 'name' | 'sku' | 'type' | 'lastUpdatedAt';

/**
 * Filter parameters for the items list.
 */
export interface ItemsFilter {
  type?: ItemTypeFilter;
  search?: string;
  page?: number;
  limit?: number;
  sort?: ItemSortField;
  direction?: SortDirection;
  archived?: boolean;
  excludeLinkedIntegration?: string;
}

/**
 * URL search params schema for items routes.
 */
export const itemsSearchParamsSchema = z.object({
  query: searchTermSchema,
  sort: z.enum(['name', 'sku', 'type', 'lastUpdatedAt']).optional(),
  direction: sortDirectionSchema.optional(),
  page: z.number().optional(),
  archived: z.boolean().optional(),
  type: z.enum(['service', 'device', 'bulk']).optional(),
  new: z.string().optional(),
  newType: z.enum(['service', 'device', 'bulk']).optional(),
});

export type ItemsSearchParams = z.infer<typeof itemsSearchParamsSchema>;

/**
 * Schema for item field definition.
 */
export const itemFieldSchema = z.object({
  id: z.string(),
  key: z.string(),
  type: productFieldTypeSchema,
  label: z.string(),
  order: z.number(),
  section: z.number(),
  metadataCount: z.number(),
});

export type ItemField = z.infer<typeof itemFieldSchema>;

/**
 * Schema for item accounting defaults.
 */
const itemAccountingSchema = z.object({
  salvageValue: currencySchema.nullable(),
  usefulLife: z.number().nullable(),
  depreciationModel: z.string().nullable(),
  simpleCostBasis: currencySchema.nullable(),
  billOfMaterials: currencySchema.nullable(),
  directLabor: currencySchema.nullable(),
  allocatedIndirect: currencySchema.nullable(),
  freightInbound: currencySchema.nullable(),
  freightOutbound: currencySchema.nullable(),
  installation: currencySchema.nullable(),
  tax: currencySchema.nullable(),
  tariffs: currencySchema.nullable(),
  interest: currencySchema.nullable(),
  totalCOGS: currencySchema.nullable(),
  costCurrencyCode: normalizedIso(isoCurrencyCodeSchema).nullable(),
});

export type ItemAccounting = z.infer<typeof itemAccountingSchema>;

/**
 * Schema for item pricing defaults.
 */
const itemPricingSchema = z.object({
  pricingMode: pricingModeSchema.nullable(),
  unitPrice: currencySchema.nullable(),
  fixedPrice: currencySchema.nullable(),
  chargeSchedule: chargeScheduleSchema.nullable(),
  chargeFrequency: billingFrequencySchema.nullable(),
  invoiceMode: invoiceModeSchema.nullable(),
  invoiceFrequency: billingFrequencySchema.nullable(),
});

export type ItemPricing = z.infer<typeof itemPricingSchema>;

/**
 * Schema for BOM items in the item detail.
 * Uses `item` field to support both bulk items (parts) and potentially
 * devices in the future when that functionality is added.
 */
const itemBomSchema = z.object({
  quantity: z.number(),
  item: sparseItemSchema,
});

/**
 * Schema for the detailed item response.
 * Includes fields, accounting, and BOM data for the item detail page.
 */
export const itemDetailSchema = z.object({
  id: z.string(),
  key: z.string(),
  type: itemTypeSchema,
  sku: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  assetCount: z.number().nullable(),
  inventoryMovementCount: z.number().nullable(),
  lastUpdatedAt: dateTimeSchema.nullable(),
  lastUpdatedBy: sparsePersonSchema,
  isArchived: z.boolean(),
  integrations: integrationListSchema,
  hasAssets: z.boolean(),
  isPhysical: z.boolean(),
  shouldRenderSerialFields: z.boolean(),
  fields: z.array(itemFieldSchema),
  accounting: itemAccountingSchema,
  defaultPricing: itemPricingSchema.nullable(),
  bom: z.array(itemBomSchema).nullable(),
});

export type ItemDetail = z.infer<typeof itemDetailSchema>;

/**
 * Zod schema for a BOM line item during form editing.
 * Item can be null when a new line is added but no item is selected yet.
 */
const itemBomQuantityError = 'Quantity must be at least 1';

const itemBomLineItemSchema = z.object({
  item: sparseItemSchema.nullable(),
  quantity: z
    .number({
      required_error: itemBomQuantityError,
      invalid_type_error: itemBomQuantityError,
    })
    .int()
    .min(1, itemBomQuantityError),
});

/**
 * BOM line item structure for the item BOM form.
 */
export type ItemBomLineItem = z.infer<typeof itemBomLineItemSchema>;

/**
 * Zod schema for validating BOM on form submission.
 * Ensures all items are selected (non-null) before submitting.
 */
export const itemBomFormSchema = z
  .array(itemBomLineItemSchema)
  .superRefine((items, ctx) => {
    items.forEach((lineItem, index) => {
      if (lineItem.item === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Item is required',
          path: [index, 'item'],
        });
      }
    });
  });
